import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MainTab } from 'components/core';
import styles from './ConfigLayoutRoles.module.css';
import { useRecoilState, useSetRecoilState, useRecoilValue } from 'recoil';
import {
  authState,
  currentRoleState,
  rolesState,
  showUsersRoleModalState,
  editItemState,
  userTabSelectedState,
  PermissionsList,
  usersForCurrentRoleState,
  newItemState,
} from 'states';
import { isSuperAdminRoleCode } from 'helpers';
import { NewRole } from './NewRole';
import { Users } from './Users';
import { Permissions } from './Permissions';

interface ConfigLayoutRolesProps {
  onBack?: () => void;
}

export const ConfigLayoutRoles = ({ onBack }: ConfigLayoutRolesProps) => {
  const { t } = useTranslation();
  const [currentRole, setCurrentRole] = useRecoilState(currentRoleState);
  const [roles, setRoles] = useRecoilState(rolesState);
  const setShowAddUserModal = useSetRecoilState(showUsersRoleModalState);
  const [editCurrentRole, setEditCurrentRole] = useRecoilState(editItemState);
  const setIsNewItem = useSetRecoilState(newItemState);
  const userTabSelected = useRecoilValue(userTabSelectedState);
  const permissionsList = useRecoilValue(PermissionsList);
  const usersForCurrentRole = useRecoilValue(usersForCurrentRoleState);
  const loggedInAuth = useRecoilValue(authState);
  const isLoggedInUserSuperAdmin = isSuperAdminRoleCode(loggedInAuth?.roles?.[0]);
  const [tabItems, setTabItems] = useState<any>([]);

  const subTitleText = userTabSelected
    ? t('ROLE.USERS_COUNT', { count: usersForCurrentRole.length })
    : t('ROLE.PERMISSIONS_COUNT', { count: permissionsList.length });

  const isNewOrEditing = !currentRole.id || editCurrentRole;
  const isViewMode = Boolean(currentRole.id) && !editCurrentRole;

  let roleTitleText = currentRole.name;
  if (isNewOrEditing) {
    roleTitleText = editCurrentRole ? t('ROLE.UPDATE_ROLE') : t('ROLE.NEW_ROLE');
  }

  const tabItem = [
    {
      id: 0,
      name: t('ROLE.PERMISSIONS_TAB'),
      target: '#nav-permissions',
      control: 'nav-permissions',
      tabId: 'nav-permissions-tab',
      content: <Permissions />,
    },
    {
      id: 1,
      name: t('ROLE.USERS_TAB'),
      target: '#nav-users',
      control: 'nav-users',
      tabId: 'nav-users-tab',
      content: <Users />,
    },
  ];

  // Re-render tabs when permissions or users lists update
  useEffect(() => {
    setTabItems([...tabItem]);
  }, [permissionsList.length, usersForCurrentRole.length, editCurrentRole, t]);

  const handleBack = () => {
    // Clean up unsaved new role from list
    const unsavedIdx = roles.findIndex((r: any) => r.id === undefined);
    if (unsavedIdx !== -1) {
      const updated = [...roles];
      updated.splice(unsavedIdx, 1);
      setRoles(updated);
      setIsNewItem(false);
    }
    setEditCurrentRole(false);
    setCurrentRole({} as any);
    // onBack in RolesHome also calls setCurrentRole — belt and suspenders
    if (onBack) onBack();
  };

  return (
    <div className={styles.detailPage}>
      {/* Header */}
      <div className={styles.detailHeader}>
        <div className={styles.headerLeft}>
          <button className={styles.backBtn} onClick={handleBack} title={t('ROLE.BACK_TO_ROLES')}>
            <span className="material-icons">arrow_back</span>
          </button>
          <div>
            <div className={styles.roleTitle}>
              {roleTitleText}
            </div>
            {isViewMode && <div className={styles.roleMeta}>{subTitleText}</div>}
          </div>
        </div>

        <div className={styles.headerActions}>
          {isViewMode && !userTabSelected && isLoggedInUserSuperAdmin && (
            <button className={styles.editBtn} onClick={() => setEditCurrentRole(true)}>
              <span className="material-icons" style={{ fontSize: 15 }}>
                edit
              </span>
              {t('ROLE.EDIT_ROLE')}
            </button>
          )}
          {isViewMode && userTabSelected && (
            <button className={styles.addUserBtn} onClick={() => setShowAddUserModal(true)}>
              <span className="material-icons" style={{ fontSize: 15 }}>
                person_add
              </span>
              {t('ROLE.ADD_USERS')}
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div className={styles.detailBody}>
        {isNewOrEditing && <NewRole />}
        {isViewMode && <MainTab tabItems={tabItems} />}
      </div>
    </div>
  );
};
